import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { projects, categories } from '../data/projects';
import ProjectCard from '../components/ProjectCard';

const Portfolio = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [filteredProjects, setFilteredProjects] = useState(projects);

  // Update filtered projects when category changes
  useEffect(() => {
    if (activeCategory === 'all') {
      setFilteredProjects(projects);
    } else {
      const filtered = projects.filter(project => project.category === activeCategory);
      setFilteredProjects(filtered);
    }
  }, [activeCategory]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  const handleCategoryFilter = (categoryId) => {
    setActiveCategory(categoryId);
  };

  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
      variants={containerVariants}
      className="max-w-6xl"
    >
      {/* Header */}
      <motion.div variants={itemVariants} className="mb-12">
        <h2 className="text-2xl lg:text-3xl font-bold text-white-1 mb-6">
          My <span className="gradient-text">Portfolio</span>
        </h2>
        <div className="w-20 h-1 bg-gradient-to-r from-accent-yellow to-orange-yellow-crayola rounded-full mb-8"></div>
        <p className="text-light-gray text-sm max-w-2xl">
          Here are some of my recent projects that showcase my skills in web development, 
          DevOps, and cloud infrastructure. Each project represents a unique challenge and solution.
        </p>
      </motion.div>

      {/* Filter Buttons */}
      <motion.div variants={itemVariants} className="mb-12">
        <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
          {categories.map((category) => (
            <motion.button
              key={category.id}
              onClick={() => handleCategoryFilter(category.id)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                activeCategory === category.id
                  ? 'bg-accent-yellow text-bg-dark'
                  : 'bg-card-dark/50 border border-jet/30 text-light-gray hover:text-white-1 hover:border-accent-yellow/30'
              }`}
            >
              {category.name}
            </motion.button>
          ))}
        </div>
      </motion.div>

      {/* Projects Grid */}
      <motion.div variants={itemVariants}>
        <AnimatePresence mode="wait">
          <motion.div
            key={activeCategory}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {filteredProjects.map((project, index) => (
              <ProjectCard
                key={project.id}
                project={project}
                index={index}
              />
            ))}
          </motion.div>
        </AnimatePresence>

        {/* Empty State */}
        {filteredProjects.length === 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-16"
          >
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-lg font-semibold text-white-1 mb-2">
              No projects found
            </h3>
            <p className="text-light-gray text-sm">
              Try selecting a different category to see more projects.
            </p>
          </motion.div>
        )}
      </motion.div>

      {/* Call to Action */}
      <motion.div 
        variants={itemVariants}
        className="mt-16 text-center bg-card-dark/30 border border-jet/30 rounded-xl p-8 backdrop-blur-sm"
      >
        <h3 className="text-lg font-bold text-white-1 mb-4">
          Interested in working together?
        </h3>
        <p className="text-light-gray text-sm mb-6 max-w-2xl mx-auto">
          I'm always open to discussing new opportunities and exciting projects. 
          Let's create something amazing together!
        </p>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
          className="px-8 py-3 bg-gradient-to-r from-accent-yellow to-orange-yellow-crayola text-bg-dark font-semibold rounded-lg hover:shadow-lg hover:shadow-accent-yellow/25 transition-all duration-300"
        >
          Get In Touch
        </motion.button>
      </motion.div>
    </motion.div>
  );
};

export default Portfolio;
